import React from 'react'

import fullReportLinkOptions from './fullReportLinkOptions'
import { useSearchParams } from '@edge/spark/utils'
import VisualizeHelper, { HandlerActions } from './VisualizeHelper'
import { REPORT_RESOURCE_PATHS } from '../ReportResourcePaths'
import { useJasperLogin } from './useJasperLogin'
import useCheckVisualizeLoaded from './useCheckVisualizeLoaded'

const FORCE_JASPER_LOGIN = 'REPORT_PATH_PLACEHOLDER' // TEMP

export const useJasperReport = (): any => {
    const [helper, setHelper] = React.useState<VisualizeHelper>()
    const [totalPages, setTotalPages] = React.useState(1)
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState<unknown>()
    const isVisualizeLoaded = useCheckVisualizeLoaded()

    const { report, startDate, endDate, dealerId, yearWeek } = useSearchParams()
    const eventHandler = React.useCallback((value: number | { message: string }, eventName: HandlerActions): void => {
        setError(undefined)
        console.debug(eventName, value)

        switch (eventName) {
            case HandlerActions.CHANGE_TOTAL_PAGES:
                if (typeof value === 'number') {
                    setTotalPages(value)
                } else {
                    throw new Error(`Unhandled event type: ${eventName} ${JSON.stringify(value)}`)
                }
                return
            case HandlerActions.CLEAR_ERRORS:
                setError(undefined)
                break
            case HandlerActions.ERROR:
                if (!loading) {
                    if (typeof value === 'object') {
                        setError(value)
                    } else {
                        throw new Error(`Unhandled event type: ${eventName} ${JSON.stringify(value)}`)
                    }
                }
                return
            case HandlerActions.LOADING_TRUE:
                setError(undefined)
                return
            case HandlerActions.LOADING_FALSE:
                return
            default:
                throw new Error(`Unhandled event type: ${JSON.stringify({ eventName, value })}`)
        }
    }, [])

    const type = REPORT_RESOURCE_PATHS[report]?.type
    useJasperLogin() // calls a hook that logs into Jasper

    React.useEffect(() => {
        if (isVisualizeLoaded) {
            ;(async () => {
                setError(undefined)
                await getReport()
            })()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, report, helper, isVisualizeLoaded])

    React.useEffect(() => {
        const visualizeHelper = new VisualizeHelper(eventHandler)
        setHelper(visualizeHelper)
    }, [eventHandler, isVisualizeLoaded])

    async function getReport() {
        try {
            if (isVisualizeLoaded && helper) {
                const reportSelected = report && Boolean(report.length)
                const reportPath = (reportSelected && REPORT_RESOURCE_PATHS[report]?.path) || FORCE_JASPER_LOGIN
                const linkOptions = type === 'full' ? fullReportLinkOptions : undefined
                const params = {
                    selected_end_date: [endDate],
                    selected_start_date: [startDate],
                    dealer_id: [dealerId],
                    year_week: [yearWeek],
                }

                await helper.runReport(reportPath, params, linkOptions)

                setLoading(false)
            }
        } catch (error) {
            console.error(error)
            setLoading(false)
        }
    }

    return {
        helper,
        totalPages,
        loading,
        error,
        type,
    }
}
